import React, { useState } from "react";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

interface ProactiveRule {
  id: number;
  name: string;
  ruleType: string;
  signalConfig: string;
  pageTargeting: string;
  knowledgeSources: string;
  messageStrategy: string;
  messageTemplate: string | null;
  aiPromptOverride: string | null;
  cooldownMinutes: number;
  priority: number;
  maxPerSession: number;
  maxPerVisitor: number;
  isEnabled: boolean;
}

interface Props {
  rule: ProactiveRule | null;
  onClose: () => void;
  onSaved: () => void;
}

const RULE_TYPES = [
  { value: "page_context", label: "Page Context", desc: "Trigger based on what page the visitor is viewing" },
  { value: "cart_state", label: "Cart State", desc: "Trigger based on cart contents or checkout hesitation" },
  { value: "return_visitor", label: "Return Visitor", desc: "Trigger when a known visitor returns to the site" },
  { value: "time_based", label: "Time-Based", desc: "Trigger after visitor dwells on a page for X seconds" },
  { value: "scroll_depth", label: "Scroll Depth", desc: "Trigger when visitor scrolls past a threshold" },
  { value: "custom", label: "Custom", desc: "Combine multiple signal types" },
];

const MESSAGE_STRATEGIES = [
  { value: "ai_generated", label: "AI Generated", desc: "AI creates the message from your knowledge base" },
  { value: "template", label: "Template", desc: "Use a fixed template with variable substitution" },
  { value: "hybrid", label: "Hybrid", desc: "AI fills in template placeholders with contextual data" },
];

interface SignalConfig {
  dwellTimeSeconds?: number;
  scrollDepthPercent?: number;
  cartValueMin?: number;
  isReturnVisitor?: boolean;
  checkoutHesitationSeconds?: number;
}

interface PageTarget {
  type: string;
  value: string;
}

interface KnowledgeSources {
  useProductIndex: boolean;
  useKbArticles: boolean;
  useConversationTraining: boolean;
  useScrapedPages: boolean;
  useYouTubeTranscripts: boolean;
  maxChunks: number;
}

function safeParseJson<T>(json: string | null | undefined, fallback: T): T {
  if (!json) return fallback;
  try { return JSON.parse(json) as T; } catch { return fallback; }
}

export default function ProactiveRuleEditor({ rule, onClose, onSaved }: Props) {
  const api = useApi();
  const { displayToast } = useToastAlert();
  const isEditing = !!rule;

  const [name, setName] = useState(rule?.name ?? "");
  const [ruleType, setRuleType] = useState(rule?.ruleType ?? "page_context");
  const [messageStrategy, setMessageStrategy] = useState(rule?.messageStrategy ?? "ai_generated");
  const [messageTemplate, setMessageTemplate] = useState(rule?.messageTemplate ?? "");
  const [aiPromptOverride, setAiPromptOverride] = useState(rule?.aiPromptOverride ?? "");
  const [cooldownMinutes, setCooldownMinutes] = useState(rule?.cooldownMinutes ?? 30);
  const [priority, setPriority] = useState(rule?.priority ?? 10);
  const [maxPerSession, setMaxPerSession] = useState(rule?.maxPerSession ?? 1);
  const [maxPerVisitor, setMaxPerVisitor] = useState(rule?.maxPerVisitor ?? 3);
  const [saving, setSaving] = useState(false);

  const [signals, setSignals] = useState<SignalConfig>(
    safeParseJson<SignalConfig>(rule?.signalConfig, {
      dwellTimeSeconds: 45,
      scrollDepthPercent: 80,
    })
  );

  const [pageTargets, setPageTargets] = useState<PageTarget[]>(
    safeParseJson<PageTarget[]>(rule?.pageTargeting, [{ type: "all_pages", value: "" }])
  );

  const [knowledgeSources, setKnowledgeSources] = useState<KnowledgeSources>(
    safeParseJson<KnowledgeSources>(rule?.knowledgeSources, {
      useProductIndex: true,
      useKbArticles: true,
      useConversationTraining: true,
      useScrapedPages: true,
      useYouTubeTranscripts: false,
      maxChunks: 5,
    })
  );

  const handleSave = async () => {
    if (!name.trim()) {
      displayToast(Severity.Error, "Rule name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        ruleType,
        signalConfigJson: JSON.stringify(signals),
        pageTargetingJson: JSON.stringify(pageTargets),
        knowledgeSourcesJson: JSON.stringify(knowledgeSources),
        messageStrategy,
        messageTemplate: messageStrategy !== "ai_generated" ? messageTemplate : null,
        aiPromptOverride: messageStrategy !== "template" ? aiPromptOverride || null : null,
        cooldownMinutes,
        priority,
        maxPerSession,
        maxPerVisitor,
        isEnabled: rule?.isEnabled ?? true,
      };

      if (isEditing) {
        await api.admin.put(`ProactiveEngagement/${rule!.id}`, payload);
        displayToast(Severity.Success, "Rule updated");
      } else {
        await api.admin.post("ProactiveEngagement", payload);
        displayToast(Severity.Success, "Rule created");
      }
      onSaved();
    } catch {
      displayToast(Severity.Error, "Failed to save rule");
    } finally {
      setSaving(false);
    }
  };

  const addPageTarget = () => {
    setPageTargets([...pageTargets, { type: "url_contains", value: "" }]);
  };

  const removePageTarget = (idx: number) => {
    setPageTargets(pageTargets.filter((_, i) => i !== idx));
  };

  const updatePageTarget = (idx: number, field: keyof PageTarget, value: string) => {
    const updated = [...pageTargets];
    updated[idx] = { ...updated[idx], [field]: value };
    setPageTargets(updated);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto pt-8 pb-8">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold">
            {isEditing ? "Edit Rule" : "New Proactive Rule"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <i className="fa-solid fa-xmark text-lg" />
          </button>
        </div>

        <div className="p-5 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium mb-1">Rule Name</label>
            <input
              type="text"
              className="input w-full"
              placeholder="e.g., Checkout Hesitation Helper"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* Rule Type */}
          <div>
            <label className="block text-sm font-medium mb-2">Trigger Type</label>
            <div className="grid grid-cols-2 gap-2">
              {RULE_TYPES.map((rt) => (
                <button
                  key={rt.value}
                  onClick={() => setRuleType(rt.value)}
                  className={`text-left p-3 rounded-lg border transition-colors ${
                    ruleType === rt.value
                      ? "border-cornflower-blue-500 bg-cornflower-blue-50 dark:bg-cornflower-blue-900/20"
                      : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                  }`}
                >
                  <div className="text-sm font-medium">{rt.label}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{rt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Signal Thresholds */}
          <div>
            <label className="block text-sm font-medium mb-2">Signal Thresholds</label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500">Dwell time (seconds)</label>
                <input
                  type="number"
                  className="input w-full mt-1"
                  value={signals.dwellTimeSeconds ?? ""}
                  onChange={(e) => setSignals({ ...signals, dwellTimeSeconds: Number(e.target.value) || undefined })}
                  placeholder="45"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Scroll depth (%)</label>
                <input
                  type="number"
                  className="input w-full mt-1"
                  value={signals.scrollDepthPercent ?? ""}
                  onChange={(e) => setSignals({ ...signals, scrollDepthPercent: Number(e.target.value) || undefined })}
                  placeholder="80"
                  min={0}
                  max={100}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Minimum cart value ($)</label>
                <input
                  type="number"
                  className="input w-full mt-1"
                  value={signals.cartValueMin ?? ""}
                  onChange={(e) => setSignals({ ...signals, cartValueMin: Number(e.target.value) || undefined })}
                  placeholder="50"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Checkout hesitation (seconds)</label>
                <input
                  type="number"
                  className="input w-full mt-1"
                  value={signals.checkoutHesitationSeconds ?? ""}
                  onChange={(e) => setSignals({ ...signals, checkoutHesitationSeconds: Number(e.target.value) || undefined })}
                  placeholder="60"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 mt-3 text-sm">
              <input
                type="checkbox"
                checked={signals.isReturnVisitor ?? false}
                onChange={(e) => setSignals({ ...signals, isReturnVisitor: e.target.checked })}
              />
              Only trigger for return visitors
            </label>
          </div>

          {/* Page Targeting */}
          <div>
            <label className="block text-sm font-medium mb-2">Page Targeting</label>
            <div className="space-y-2">
              {pageTargets.map((target, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <select
                    className="input w-40"
                    value={target.type}
                    onChange={(e) => updatePageTarget(idx, "type", e.target.value)}
                  >
                    <option value="all_pages">All pages</option>
                    <option value="url_contains">URL contains</option>
                    <option value="url_equals">URL equals</option>
                    <option value="url_matches">URL matches (regex)</option>
                  </select>
                  {target.type !== "all_pages" && (
                    <input
                      type="text"
                      className="input flex-1"
                      placeholder={target.type === "url_contains" ? "/products/" : "https://..."}
                      value={target.value}
                      onChange={(e) => updatePageTarget(idx, "value", e.target.value)}
                    />
                  )}
                  {pageTargets.length > 1 && (
                    <button
                      onClick={() => removePageTarget(idx)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <i className="fa-solid fa-xmark" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button onClick={addPageTarget} className="text-xs text-cornflower-blue-500 hover:underline mt-2">
              + Add page condition
            </button>
          </div>

          {/* Knowledge Sources */}
          <div>
            <label className="block text-sm font-medium mb-2">Knowledge Sources</label>
            <p className="text-xs text-gray-400 mb-3">
              Select which indexed data sources the AI uses to build the proactive message context.
            </p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: "useProductIndex" as const, label: "Product Catalog", icon: "fa-shopping-bag" },
                { key: "useKbArticles" as const, label: "Knowledge Base", icon: "fa-book" },
                { key: "useConversationTraining" as const, label: "Past Conversations", icon: "fa-comments" },
                { key: "useScrapedPages" as const, label: "Scraped Pages", icon: "fa-globe" },
                { key: "useYouTubeTranscripts" as const, label: "YouTube Transcripts", icon: "fa-video" },
              ].map((src) => (
                <label
                  key={src.key}
                  className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                    knowledgeSources[src.key]
                      ? "border-cornflower-blue-500 bg-cornflower-blue-50 dark:bg-cornflower-blue-900/20"
                      : "border-gray-200 dark:border-gray-700"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={knowledgeSources[src.key]}
                    onChange={(e) => setKnowledgeSources({ ...knowledgeSources, [src.key]: e.target.checked })}
                  />
                  <i className={`fa-solid ${src.icon} text-sm text-gray-400`} />
                  <span className="text-sm">{src.label}</span>
                </label>
              ))}
            </div>
            <div className="mt-3">
              <label className="text-xs text-gray-500">Max knowledge chunks per query</label>
              <input
                type="number"
                className="input w-24 mt-1"
                value={knowledgeSources.maxChunks}
                onChange={(e) => setKnowledgeSources({ ...knowledgeSources, maxChunks: Number(e.target.value) || 5 })}
                min={1}
                max={20}
              />
            </div>
          </div>

          {/* Message Strategy */}
          <div>
            <label className="block text-sm font-medium mb-2">Message Strategy</label>
            <div className="space-y-2">
              {MESSAGE_STRATEGIES.map((ms) => (
                <label
                  key={ms.value}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    messageStrategy === ms.value
                      ? "border-cornflower-blue-500 bg-cornflower-blue-50 dark:bg-cornflower-blue-900/20"
                      : "border-gray-200 dark:border-gray-700"
                  }`}
                >
                  <input
                    type="radio"
                    name="messageStrategy"
                    value={ms.value}
                    checked={messageStrategy === ms.value}
                    onChange={() => setMessageStrategy(ms.value)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-medium">{ms.label}</div>
                    <div className="text-xs text-gray-400">{ms.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Template (if template or hybrid) */}
          {messageStrategy !== "ai_generated" && (
            <div>
              <label className="block text-sm font-medium mb-1">Message Template</label>
              <textarea
                className="input w-full h-24"
                placeholder="Hey! I see you're looking at {product_name}. {top_question_answer}"
                value={messageTemplate}
                onChange={(e) => setMessageTemplate(e.target.value)}
              />
              <p className="text-xs text-gray-400 mt-1">
                Variables: {"{product_name}"}, {"{price}"}, {"{top_question}"}, {"{shipping_info}"}, {"{related_products}"}
              </p>
            </div>
          )}

          {/* AI Prompt Override (if ai_generated or hybrid) */}
          {messageStrategy !== "template" && (
            <div>
              <label className="block text-sm font-medium mb-1">AI Prompt Override (optional)</label>
              <textarea
                className="input w-full h-20"
                placeholder="Leave blank for default. Override to customize the AI's tone or focus."
                value={aiPromptOverride}
                onChange={(e) => setAiPromptOverride(e.target.value)}
              />
            </div>
          )}

          {/* Limits */}
          <div>
            <label className="block text-sm font-medium mb-2">Rate Limits</label>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-gray-500">Cooldown (minutes)</label>
                <input
                  type="number"
                  className="input w-full mt-1"
                  value={cooldownMinutes}
                  onChange={(e) => setCooldownMinutes(Number(e.target.value) || 30)}
                  min={1}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Max per session</label>
                <input
                  type="number"
                  className="input w-full mt-1"
                  value={maxPerSession}
                  onChange={(e) => setMaxPerSession(Number(e.target.value) || 1)}
                  min={0}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Max per visitor (total)</label>
                <input
                  type="number"
                  className="input w-full mt-1"
                  value={maxPerVisitor}
                  onChange={(e) => setMaxPerVisitor(Number(e.target.value) || 3)}
                  min={0}
                />
              </div>
            </div>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium mb-1">Priority</label>
            <input
              type="number"
              className="input w-24"
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value) || 10)}
              min={1}
              max={100}
            />
            <p className="text-xs text-gray-400 mt-1">
              Lower number = higher priority. When multiple rules match, the highest priority fires first.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button onClick={handleSave} className="btn btn-primary" disabled={saving}>
            {saving ? "Saving..." : isEditing ? "Update Rule" : "Create Rule"}
          </button>
        </div>
      </div>
    </div>
  );
}
